import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ConfirmDialog } from './confirm-dialog';

/*
 * Imperative confirmation helper built on the declarative `ConfirmDialog`.
 * Call `const confirm = useConfirm()` then `await confirm({ ... })` — it resolves
 * to `true` (confirmed) or `false` (cancelled/dismissed). Handy where many
 * one-off confirms would otherwise each need their own `open` state.
 */

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  tone?: 'danger' | 'default';
  confirmLabel?: string;
  cancelLabel?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export const ConfirmProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((answer: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    async (next) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setOptions(next);
      }),
    []
  );

  const settle = useCallback((answer: boolean): void => {
    setOptions(null);
    resolverRef.current?.(answer);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <ConfirmDialog
          open
          title={options.title}
          message={options.message}
          tone={options.tone}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          onCancel={(): void => {
            settle(false);
          }}
          onConfirm={(): void => {
            settle(true);
          }}
        />
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const confirm = useContext(ConfirmContext);

  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }

  return confirm;
};
